<script lang="ts">
  import type { CaptureTally, Color, PieceTally, PieceType } from '@blind-chess/shared';
  import { pieceGlyph } from './pieces.js';

  interface Props {
    captures: CaptureTally;
    you: Color;
  }
  let { captures, you }: Props = $props();

  const ORDER: PieceType[] = ['q', 'r', 'b', 'n', 'p'];
  const oppColor = $derived<Color>(you === 'w' ? 'b' : 'w');

  function glyphs(tally: PieceTally, color: Color): { glyph: string; key: string }[] {
    const out: { glyph: string; key: string }[] = [];
    for (const t of ORDER) {
      const n = tally[t] ?? 0;
      for (let i = 0; i < n; i++) out.push({ glyph: pieceGlyph({ color, type: t }), key: `${t}${i}` });
    }
    return out;
  }
  function total(tally: PieceTally): number {
    return Object.values(tally).reduce((a, b) => a + b, 0);
  }

  const youTook = $derived(glyphs(captures.byYou, oppColor));
  const youLost = $derived(glyphs(captures.byOpponent, you));
</script>

<div class="panel">
  <header>Captures</header>
  <div class="row">
    <span class="label">You took</span>
    <span class="pieces">
      {#each youTook as g (g.key)}<span class="g g-{oppColor}">{g.glyph}</span>{:else}<span class="muted">—</span>{/each}
    </span>
    <span class="n">{total(captures.byYou)}</span>
  </div>
  <div class="row lost">
    <span class="label">Lost</span>
    <span class="pieces">
      {#each youLost as g (g.key)}<span class="g g-{you}">{g.glyph}</span>{:else}<span class="muted">—</span>{/each}
    </span>
    <span class="n">{total(captures.byOpponent)}</span>
  </div>
</div>

<style>
  .panel {
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 8px;
  }
  header {
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-dim);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    border-bottom: 1px solid var(--border);
  }
  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
  }
  .row.lost { opacity: 0.7; }
  .label {
    font-size: 12px;
    color: var(--text-dim);
    width: 56px;
    flex-shrink: 0;
  }
  .pieces { flex: 1; font-size: 20px; line-height: 1; }
  .g-w { color: #fafafa; }
  /* Dark pieces need a light outline to read on the dark panel background. */
  .g-b { color: #1a1a1a; text-shadow: 0 0 2px var(--text), 0 0 2px var(--text); }
  .n {
    font-family: ui-monospace, monospace;
    font-size: 13px;
    color: var(--text-dim);
  }
</style>
